/**
 * New skill (Skills UX pass 2026-09-06). A title is enough; the rest sits
 * under two named groups — "Helps me map the path" (where you are, where you
 * want to get, by when, what it belongs to or which parts of life, priority)
 * and "Helps me keep you going" (why it matters, how often you will
 * practise). The form offers what the skill page's Add row offers, and
 * nothing else.
 */
import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Wrench } from 'lucide-react'
import { HierarchyCreationForm, type BasicField, type FieldGroup } from '../components/hierarchy/HierarchyCreationForm'
import { SkillService } from '../services/skillService'
import { useToast } from '../components/ui/Toast'
import { useLifeAreaOptions } from '../hooks/useLifeAreas'
import { LIFE_AREA_OPTIONS, lifeAreaLabel, lifeAreaValue } from '../utils/taskForm'
import { CADENCE_OPTIONS, LEVEL_OPTIONS, SKILL_PRIORITY_OPTIONS } from '../utils/skillOptions'
import { skillFormToPayload, type SkillFormData } from '../utils/skillForm'

const PATH_GROUP: FieldGroup = { key: 'path', title: 'Helps me map the path', blurb: 'Where you are and where you want to get. Skip what you do not know yet.' }
const KEEP_GROUP: FieldGroup = { key: 'keep', title: 'Helps me keep you going', blurb: 'Why it matters and how often you will practise, in your own words.' }

const LEVEL_CHIPS = LEVEL_OPTIONS.map((o) => ({ value: o.value, label: o.label }))

const FIELDS: BasicField[] = [
  { name: 'title', label: 'What is the skill?', type: 'text', required: true, placeholder: 'e.g., Public speaking', section: 'basic' },
  { name: 'current_level', span: 'half', label: 'Where you are now', type: 'chips', options: LEVEL_CHIPS, group: PATH_GROUP, section: 'basic',
    liveHint: (v) => LEVEL_OPTIONS.find((o) => o.value === v)?.capability || null },
  { name: 'target_level', span: 'half', label: 'Where you want to get', type: 'chips', options: LEVEL_CHIPS, group: PATH_GROUP, section: 'basic',
    liveHint: (v) => LEVEL_OPTIONS.find((o) => o.value === v)?.capability || null },
  { name: 'target_date', span: 'half', label: 'Get there by', type: 'date', group: PATH_GROUP, section: 'basic', helpText: 'A rough date is fine. When it passes, I will ask how it is going.' },
  { name: 'priority', span: 'half', label: 'Priority', type: 'chips', options: SKILL_PRIORITY_OPTIONS, group: PATH_GROUP, section: 'basic' },
  { name: 'parent_link', span: 'half', label: 'Belongs to', type: 'parent_picker', allowedParentTypes: ['goal'], group: PATH_GROUP, section: 'basic' },
  {
    name: 'life_areas', label: 'Or, if it is just for you — which parts of life?', type: 'chips', multi: true, options: LIFE_AREA_OPTIONS,
    allowCustom: true, customLabel: 'Another…', customPlaceholder: 'e.g. Side project', customValue: lifeAreaValue, labelOf: lifeAreaLabel,
    helpText: 'Pick one or more; the first is the main one. Name your own if none of these fits.',
    showWhen: (f) => !f.parent_id, group: PATH_GROUP, section: 'basic',
  },
  { name: 'description', label: 'Why it matters', type: 'text', placeholder: 'What having this skill would change — one line is plenty', group: KEEP_GROUP, section: 'basic' },
  { name: 'practice_cadence', label: 'Practise how often', type: 'chips', options: CADENCE_OPTIONS, group: KEEP_GROUP, section: 'basic' },
]

export const CreateSkill: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const seededTitle = (location.state as { title?: string } | null)?.title || ''
  const [formData, setFormData] = useState<SkillFormData>({ title: seededTitle, priority: 'medium', parent_type: '', parent_id: null })
  const [formError, setFormError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const skillService = useMemo(() => new SkillService(), [])

  const picked: string[] = Array.isArray(formData.life_areas) ? formData.life_areas.map(String) : []
  const { options: lifeAreaOptions, remember: rememberLifeArea } = useLifeAreaOptions(picked)
  const fields = useMemo(() => FIELDS.map((f) => (f.name === 'life_areas' ? { ...f, options: lifeAreaOptions } : f)), [lifeAreaOptions])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (!String(formData.title || '').trim()) {
      setFormError('Give it a name — that is the one thing I need.')
      return
    }
    setIsLoading(true)
    try {
      const created = await skillService.createSkill(skillFormToPayload(formData))
      picked.forEach(rememberLifeArea)
      toast.success('Skill added', created.title)
      navigate(created?.id ? `/skills/${created.id}` : '/skills')
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add the skill')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <HierarchyCreationForm
      title="New skill"
      description="A name is enough. Where you are and where you want to get can be added on the skill itself, whenever you know."
      icon={<Wrench className="h-8 w-8 text-brand" />}
      basicFields={fields}
      advancedFields={[]}
      compactHeader
      formData={formData}
      onFormDataChange={setFormData}
      onSubmit={handleSubmit}
      submitLabel="Save"
      error={formError}
      primaryColor="indigo"
      backUrl="/skills"
      isLoading={isLoading}
    />
  )
}

export default CreateSkill
